import { Navigate, Route, Routes } from "react-router-dom";
import { useEffect } from "react";
import PrimeReact, { addLocale, locale } from "primereact/api";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import "primeflex/primeflex.css";
import "./App.scss";
import { useAuthContext } from "./providers/contextIndex";
import { redirectToSSOLogin } from "../utils/ssoRedirect";
import Home from "../features/content/pages/Home";
import MyAccount from "../features/account/pages/MyAccount";
import GestorPaginasInfo from "../features/content/pages/GestorPaginasInfo";
import QuoteForm from "../features/quote/pages/QuoteForm";
import Catalog from "../features/catalog/pages/Catalog";
import ProductDetail from "../features/catalog/pages/ProductDetail";
import Cart from "../features/cart/pages/Cart";
import CheckoutFlow from "../features/checkout/pages/CheckoutFlow";
import CheckoutProcessing from "../features/checkout/components/CheckoutProcessing";
import LandingLayout from "../shared/layouts/LandingLayout";
import AdminPagesLayout from "../features/admin/layout/AdminPagesLayout";
import Familias from "../features/admin/pages/Familias";
import ProductosFamilia from "../features/admin/pages/ProductosFamilia";
import Sucursales from "../features/admin/pages/Sucursales";
import Banners from "../features/admin/pages/Banners";
import Usuarios from "../features/admin/pages/Usuarios";
import Destacados from "../features/admin/pages/Destacados";
import Categorias from "../features/admin/pages/Categorias";
import Reportes from "../features/admin/pages/Reportes";
import DynamicContent from "../features/content/pages/DynamicContent";
import NotFound from "../shared/pages/NotFound";

PrimeReact.ripple = true;

addLocale('es', {
    firstDayOfWeek: 1,
    dayNames: ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'],
    dayNamesShort: ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'],
    dayNamesMin: ['D', 'L', 'M', 'X', 'J', 'V', 'S'],
    monthNames: ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'],
    monthNamesShort: ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'],
    today: 'Hoy',
    clear: 'Limpiar',
    startsWith: 'Comienza con',
    contains: 'Contiene',
    notContains: 'No contiene',
    endsWith: 'Termina con',
    equals: 'Igual a',
    notEquals: 'Diferente de',
    noFilter: 'Sin filtro',
    lt: 'Menor que',
    lte: 'Menor o igual a',
    gt: 'Mayor que',
    gte: 'Mayor o igual a',
    dateIs: 'Fecha igual a',
    dateIsNot: 'Fecha diferente a',
    dateBefore: 'Fecha antes de',
    dateAfter: 'Fecha después de',
    custom: 'Personalizado',
    apply: 'Aplicar',
    matchAll: 'Coincidir todo',
    matchAny: 'Coincidir cualquiera',
    addRule: 'Agregar regla',
    removeRule: 'Eliminar regla',
    accept: 'Sí',
    reject: 'No',
    choose: 'Escoger',
    upload: 'Subir',
    cancel: 'Cancelar',
    fileSizeTypes: ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'],
    emptyMessage: 'No se encontraron resultados',
    emptyFilterMessage: 'No se encontraron resultados'
});

locale("es");

const RedirectToSso = () => {
  useEffect(() => {
    redirectToSSOLogin();
  }, []);
  return null;
};

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated } = useAuthContext();
  if (!isAuthenticated) {
    return <RedirectToSso />;
  }
  return children;
};

const AdminRoute = ({ children }) => {
  const { isAuthenticated, appPerfil } = useAuthContext();
  if (!isAuthenticated || appPerfil !== "Administrador") {
    return <RedirectToSso />;
  }
  return children;
};

const ClientRoute = ({ children }) => {
  const { isAuthenticated, appPerfil } = useAuthContext();
  if (!isAuthenticated || appPerfil !== "cliente") {
    return <RedirectToSso />;
  }
  return children;
};

const App = () => {
  return (
    <Routes>
      <Route path="/login/b2c" element={<Navigate to="/" replace />} />
      <Route path="/login/b2b" element={<Navigate to="/" replace />} />
      <Route element={<LandingLayout />}>
        <Route path="/" element={<Home />} />
        <Route path="/catalogo" element={<Catalog />} />
        <Route path="/catalogo/detalle/:code" element={<ProductDetail />} />

        <Route
          element={
            <AdminRoute>
              <AdminPagesLayout />
            </AdminRoute>
          }
        >
          <Route path="/administracion/familias" element={<Familias />} />
          <Route path="/administracion/familias/:familiaId/productos" element={<ProductosFamilia />} />
          <Route path="/administracion/sucursales" element={<Sucursales />} />
          <Route path="/administracion/banners" element={<Banners />} />
          <Route path="/administracion/usuarios" element={<Usuarios />} />
          <Route path="/administracion/destacados" element={<Destacados />} />
          <Route path="/administracion/categorias" element={<Categorias />} />
          <Route path="/administracion/reportes" element={<Reportes />} />
        </Route>

        <Route path="/categoria/:collectionSlug" element={<Catalog />} />
        <Route
          path="/categoria/:collectionSlug/:subcollectionSlug"
          element={<Catalog />}
        />
        <Route
          path="/categoria/:collectionSlug/:subcollectionSlug/:familySlug"
          element={<Catalog />}
        />

        <Route
          path="/carro"
          element={
            <ProtectedRoute>
              <Cart />
            </ProtectedRoute>
          }
        />

        <Route
          path="/checkout"
          element={
            <ClientRoute>
              <CheckoutFlow step="checkout" />
            </ClientRoute>
          }
        />
        <Route
          path="/checkout/getnet"
          element={
            <ClientRoute>
              <CheckoutFlow step="checkout" />
            </ClientRoute>
          }
        />
        <Route
          path="/checkout/procesando"
          element={
            <ClientRoute>
              <CheckoutProcessing />
            </ClientRoute>
          }
        />
        <Route
          path="/checkout/success"
          element={
            <ClientRoute>
              <CheckoutFlow step="success" />
            </ClientRoute>
          }
        />
        <Route
          path="/checkout/exito"
          element={
            <ClientRoute>
              <CheckoutFlow step="success" />
            </ClientRoute>
          }
        />
        <Route
          path="/checkout/rechazado"
          element={
            <ClientRoute>
              <CheckoutFlow step="rejected" />
            </ClientRoute>
          }
        />

        <Route
          path="/mi-cuenta"
          element={
            <ProtectedRoute>
              <MyAccount />
            </ProtectedRoute>
          }
        />
        <Route path="/cotizar" element={<QuoteForm />} />
        <Route
          path="/gestor-paginas-info"
          element={
            <AdminRoute>
              <GestorPaginasInfo />
            </AdminRoute>
          }
        />
        <Route path="/info/:slug" element={<DynamicContent />} />
      </Route>
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

export default App;
